import React, { useContext } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Typography, Button, Paper, Avatar } from '@mui/material';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import BlockIcon from '@mui/icons-material/Block';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';

// ** Styled Components
const MainContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(4)
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  maxWidth: '500px',
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[6], // 또는 theme.customShadows.z6 (설정에 따라 다름)
  backgroundColor: theme.palette.background.paper,
}));

const IconAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  marginBottom: theme.spacing(4),
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.error.main,
  '& svg': {
    fontSize: '3rem'
  }
}));

const UserInfoBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.action.hover, // 은은한 배경색
  width: '100%',
  border: `1px solid ${theme.palette.divider}`,
}));

const NotAuthorized = ({ intendedGroupCode }) => {
  const theme = useTheme();
  const { user } = useSelector((state) => state.auth);
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogoutAndRedirect = async () => {
    // 1. 의도했던 그룹의 로그인 페이지 경로를 쿠키에 설정
    if (intendedGroupCode) {
      document.cookie = `groupLoginPagePath=/login/${intendedGroupCode}; path=/; max-age=31536000`;
    }
    // 2. 로그아웃 실행
    await logout();
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
        navigate(-1);
    } else {
        navigate('/');
    }
  };

  return (
    <MainContainer>
      <ContentPaper elevation={0}>
        <IconAvatar variant="rounded">
          <BlockIcon />
        </IconAvatar>

        <Typography variant="h4" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
          접근 권한이 없습니다
        </Typography>
        
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          현재 계정으로는 이 페이지에 접근할 수 없습니다.<br />
          소속된 그룹이나 프로젝트 권한을 확인해주세요.
        </Typography>

        {user && (
          <UserInfoBox>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Current Session
            </Typography>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {user.user_name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Group: <strong>{user.group_name}</strong> ({user.group_code})
            </Typography>

            {intendedGroupCode && user.group_code !== intendedGroupCode && (
               <Box sx={{ mt: 2, pt: 2, borderTop: `1px dashed ${theme.palette.divider}` }}>
                 <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500 }}>
                   ⚠️ Target Group: {intendedGroupCode}
                 </Typography>
               </Box>
            )}
          </UserInfoBox>
        )}

        <Box sx={{ display: 'flex', gap: 2, width: '100%', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button 
              fullWidth
              variant="outlined" 
              color="secondary"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
            >
              뒤로 가기
            </Button>
            
            <Button 
              fullWidth
              variant="contained" 
              color="primary"
              startIcon={<LogoutIcon />}
              onClick={handleLogoutAndRedirect}
              sx={{ boxShadow: theme.shadows[2] }}
            >
              로그아웃 및 이동
            </Button>
        </Box>
      </ContentPaper>
    </MainContainer>
  );
};

export default NotAuthorized;
